// ABOUTME: Feeds the recap column from the dialog state: hierarchy and branch labels, condition field names,
// ABOUTME: the action sentences (link ids resolved to names) and the "Open in list view" link of the scope.

import { buildListFiltersPath } from '@modules/list-filters';
import { resolveRedirectPath } from '@src/_new/utils/redirect';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useHierarchyList, useNodesById } from '../hooks/useBulkChangeNodes';
import { BulkChangeAction, FindReplaceOptions, ScopePreview } from '../types';
import { isActionComplete } from '../utils/actionDrafts';
import {
    CODE_FIELD,
    contentFieldKey,
    LIST_NAME_FIELD,
    STATUS_FIELD,
} from '../utils/conditionOperators';
import { ContentTypeLabeler } from '../utils/contentTypeLabels';
import { describeAction } from '../utils/describeAction';
import {
    ancestorFilter,
    buildScope,
    hasIncompleteConditions,
    ScopeDraft,
} from '../utils/scopeDraft';
import { nodeOptionLabel } from './NodePicker';
import { RecapSidebar } from './RecapSidebar';

type BulkChangeRecapProps = {
    editing: 'scope' | 'actions' | null;
    draft: ScopeDraft;
    actions: BulkChangeAction[];
    options: FindReplaceOptions | null;
    labeler: ContentTypeLabeler;
    preview: ScopePreview | null;
    previewLoading: boolean;
    onEditScope?: () => void;
};

const linkIdsOf = (action: BulkChangeAction): string[] => {
    if (action.kind === 'addLinks' || action.kind === 'removeLinks') return action.nodeIds;
    if (action.kind === 'replaceLink') return [action.fromNodeId, action.toNodeId].filter(Boolean);
    return [];
};

export function BulkChangeRecap({
    editing,
    draft,
    actions,
    options,
    labeler,
    preview,
    previewLoading,
    onEditScope,
}: BulkChangeRecapProps) {
    const intl = useIntl();
    const { formatMessage } = intl;
    const { hierarchies } = useHierarchyList(!draft.hierarchyId);

    const hierarchyLabel =
        hierarchies.find(hierarchy => hierarchy.id === draft.hierarchyId)?.code ??
        labeler.nameLabel(options?.objectSchemaName ?? [], draft.hierarchyId);
    // A branch path taken from the node breadcrumb starts with the hierarchy, which the recap already names.
    const hierarchyPrefix = `${hierarchyLabel} › `;
    const branchLabel = draft.branchLabel?.startsWith(hierarchyPrefix)
        ? draft.branchLabel.slice(hierarchyPrefix.length)
        : draft.branchLabel;

    const linkTargets = useMemo(
        () =>
            actions.flatMap(action => {
                if (action.kind === 'deleteNode') return [];
                const hierarchy = labeler.contentType(action.ctg, action.ct)?.hierarchy;
                return hierarchy ? [hierarchy] : [];
            }),
        [actions, labeler],
    );
    const linkIds = useMemo(() => actions.flatMap(linkIdsOf), [actions]);
    const { byId } = useNodesById(linkTargets, linkIds);

    const actionSentences = useMemo(
        () =>
            actions.filter(isActionComplete).map(action =>
                describeAction(action, {
                    formatMessage,
                    fieldLabel: labeler.typeLabel,
                    valueLabel: (ctg, ct, value) =>
                        labeler.contentType(ctg, ct)?.type === 'boolean'
                            ? formatMessage(
                                  value === 'true'
                                      ? { id: 'bulkChange.boolean.true', defaultMessage: 'Yes' }
                                      : { id: 'bulkChange.boolean.false', defaultMessage: 'No' },
                              )
                            : labeler.optionLabel(ctg, ct, value),
                    languageLabel: labeler.languageLabel,
                    isLocalized: (ctg, ct) => labeler.contentType(ctg, ct)?.multiLanguage ?? true,
                    defaultLanguage: options?.defaultLocale ?? undefined,
                    nodeLabel: id => {
                        const node = byId.get(id);
                        return node ? nodeOptionLabel(node) : id;
                    },
                }),
            ),
        [actions, labeler, byId, formatMessage, options?.defaultLocale],
    );

    const conditionLabels = useMemo(
        () =>
            Array.from(
                new Set(
                    [
                        ...draft.conditions.map(row => row.field),
                        ...draft.lockedFilters.map(filter => filter.field),
                    ]
                        .filter(Boolean)
                        .map(field => {
                            if (field === CODE_FIELD) {
                                return formatMessage({
                                    id: 'bulkChange.field.code',
                                    defaultMessage: 'Code',
                                });
                            }
                            if (field === LIST_NAME_FIELD) {
                                return formatMessage({
                                    id: 'bulkChange.field.listName',
                                    defaultMessage: 'Name',
                                });
                            }
                            if (field === STATUS_FIELD) {
                                return formatMessage({
                                    id: 'bulkChange.field.status',
                                    defaultMessage: 'Status',
                                });
                            }
                            const contentType = options?.contentTypes.find(
                                c => contentFieldKey(c.ctg, c.ct) === field,
                            );
                            return contentType
                                ? labeler.typeLabel(contentType.ctg, contentType.ct)
                                : field;
                        }),
                ),
            ),
        [draft, options, labeler, formatMessage],
    );

    const scope = useMemo(() => buildScope(draft), [draft]);
    const scopeHierarchyId = scope?.hierarchyId;
    // The hierarchy's list page: undefined while it is resolved, null when the hierarchy has none.
    const [listPath, setListPath] = useState<string | null | undefined>(undefined);

    // Resolved ahead: the button is shown only when the hierarchy has a list page (the endpoint
    // answers 404 when it has none).
    useEffect(() => {
        setListPath(undefined);
        if (!scopeHierarchyId) return undefined;
        let current = true;
        resolveRedirectPath({ hierarchy: scopeHierarchyId })
            .then(path => {
                if (current) setListPath(path);
            })
            .catch(() => {
                if (current) setListPath(null);
            });
        return () => {
            current = false;
        };
    }, [scopeHierarchyId]);

    const openInListView = () => {
        if (!scope || !listPath) return;
        window.open(
            buildListFiltersPath(listPath, {
                label: formatMessage({
                    id: 'bulkChange.recap.listFiltersLabel',
                    defaultMessage: 'Bulk change scope',
                }),
                // The list shows the objects below the branch, as the scope does (its root apart).
                filters: [
                    ...scope.filters,
                    ...(scope.rootNodeId ? [ancestorFilter(scope.rootNodeId)] : []),
                ],
            }),
            '_blank',
        );
    };

    return (
        <RecapSidebar
            editing={editing}
            preview={preview}
            previewLoading={previewLoading}
            hierarchyLabel={hierarchyLabel}
            branchLabel={branchLabel}
            conditionLabels={conditionLabels}
            actionSentences={actionSentences}
            onEditScope={onEditScope}
            onOpenInListView={draft.hierarchyId && listPath !== null ? openInListView : undefined}
            openInListViewDisabled={!scope || !listPath || hasIncompleteConditions(draft)}
        />
    );
}
